import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bot, User, ArrowLeft, Copy, Download } from 'lucide-react';
import { motion } from 'motion/react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';

interface ChatHistory {
  id: number;
  user_id: number;
  conversation_id: string;
  message_type: string;
  message_content: string;
  timestamp: string;
  context_data: string;
}

interface ConversationViewerProps {
  conversationId: string;
  onBack: () => void;
  onContinueConversation?: (conversationId: string) => void;
}

const ConversationViewer: React.FC<ConversationViewerProps> = ({
  conversationId,
  onBack,
  onContinueConversation
}) => {
  const [messages, setMessages] = useState<ChatHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user && conversationId) {
      loadConversation();
    }
  }, [user, conversationId]);

  const loadConversation = async () => {
    if (!user) return;

    try {
      setIsLoading(true);

      const { data, error } = await window.ezsite.apis.tablePage('15991', {
        PageNo: 1,
        PageSize: 1000,
        OrderByField: 'timestamp',
        IsAsc: true,
        Filters: [
        {
          name: 'user_id',
          op: 'Equal',
          value: user.ID
        },
        {
          name: 'conversation_id',
          op: 'Equal',
          value: conversationId
        }]

      });

      if (error) {
        throw new Error(error);
      }

      setMessages(data?.List || []);

    } catch (error) {
      console.error('Error loading conversation:', error);
      toast({
        title: "Error Loading Conversation",
        description: "Failed to load the conversation. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copyConversation = async () => {
    try {
      const conversationText = messages.
      map((message) => {
        const timestamp = new Date(message.timestamp).toLocaleString();
        const sender = message.message_type === 'user' ? 'You' : 'AI Assistant';
        return `[${timestamp}] ${sender}: ${message.message_content}`;
      }).
      join('\n\n');

      await navigator.clipboard.writeText(conversationText);

      toast({
        title: "Conversation Copied",
        description: "The conversation has been copied to your clipboard."
      });
    } catch (error) {
      console.error('Error copying conversation:', error);
      toast({
        title: "Copy Failed",
        description: "Failed to copy the conversation to clipboard.",
        variant: "destructive"
      });
    }
  };

  const downloadConversation = () => {
    try {
      const conversationText = messages.
      map((message) => {
        const timestamp = new Date(message.timestamp).toLocaleString();
        const sender = message.message_type === 'user' ? 'You' : 'AI Assistant';
        return `[${timestamp}] ${sender}: ${message.message_content}`;
      }).
      join('\n\n');

      const blob = new Blob([conversationText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');

      const firstUserMessage = messages.find((m) => m.message_type === 'user');
      const filename = firstUserMessage ?
      `conversation-${firstUserMessage.message_content.substring(0, 30).replace(/[^a-zA-Z0-9]/g, '_')}.txt` :
      `conversation-${conversationId}.txt`;

      a.href = url;
      a.download = filename;
      a.click();

      URL.revokeObjectURL(url);

      toast({
        title: "Download Started",
        description: "The conversation is being downloaded as a text file."
      });
    } catch (error) {
      console.error('Error downloading conversation:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download the conversation.",
        variant: "destructive"
      });
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getConversationTitle = () => {
    const firstUserMessage = messages.find((m) => m.message_type === 'user');
    if (firstUserMessage) {
      return firstUserMessage.message_content.length > 60 ?
      firstUserMessage.message_content.substring(0, 60) + '...' :
      firstUserMessage.message_content;
    }
    return 'Conversation';
  };

  if (isLoading) {
    return (
      <Card className="h-full" data-id="6ye707z76" data-path="src/components/ai/ConversationViewer.tsx">
        <CardHeader data-id="o71t9yaul" data-path="src/components/ai/ConversationViewer.tsx">
          <div className="flex items-center gap-2" data-id="gx01runfg" data-path="src/components/ai/ConversationViewer.tsx">
            <Button variant="ghost" size="sm" onClick={onBack} data-id="b0wthwosy" data-path="src/components/ai/ConversationViewer.tsx">
              <ArrowLeft className="h-4 w-4" data-id="aytg0h7mn" data-path="src/components/ai/ConversationViewer.tsx" />
            </Button>
            <CardTitle data-id="nkyjh5fva" data-path="src/components/ai/ConversationViewer.tsx">Loading Conversation...</CardTitle>
          </div>
        </CardHeader>
        <CardContent data-id="8qohjjmx9" data-path="src/components/ai/ConversationViewer.tsx">
          <div className="space-y-4" data-id="li1kfxmaa" data-path="src/components/ai/ConversationViewer.tsx">
            {[1, 2, 3, 4].map((i) =>
            <div key={i} className="animate-pulse" data-id="8go42cwg6" data-path="src/components/ai/ConversationViewer.tsx">
                <div className="flex items-start gap-3" data-id="g011d3w4s" data-path="src/components/ai/ConversationViewer.tsx">
                  <div className="h-8 w-8 bg-gray-200 rounded-full" data-id="xixsqsy89" data-path="src/components/ai/ConversationViewer.tsx"></div>
                  <div className="flex-1 space-y-2" data-id="xuqtrel3w" data-path="src/components/ai/ConversationViewer.tsx">
                    <div className="h-4 bg-gray-200 rounded w-3/4" data-id="tfwe1przu" data-path="src/components/ai/ConversationViewer.tsx"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2" data-id="enuun2tjt" data-path="src/components/ai/ConversationViewer.tsx"></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>);

  }

  return (
    <Card className="h-full" data-id="yin42c1cm" data-path="src/components/ai/ConversationViewer.tsx">
      <CardHeader data-id="ra3qfw37o" data-path="src/components/ai/ConversationViewer.tsx">
        <div className="flex items-center justify-between" data-id="skv8boqr2" data-path="src/components/ai/ConversationViewer.tsx">
          <div className="flex items-center gap-2" data-id="p2rgqkysj" data-path="src/components/ai/ConversationViewer.tsx">
            <Button variant="ghost" size="sm" onClick={onBack} data-id="x8whv2i8r" data-path="src/components/ai/ConversationViewer.tsx">
              <ArrowLeft className="h-4 w-4" data-id="lv5icw2kf" data-path="src/components/ai/ConversationViewer.tsx" />
            </Button>
            <CardTitle className="line-clamp-1 flex-1" data-id="0d90uzhec" data-path="src/components/ai/ConversationViewer.tsx">
              {getConversationTitle()}
            </CardTitle>
          </div>
          <div className="flex items-center gap-2" data-id="8qrwg2xp9" data-path="src/components/ai/ConversationViewer.tsx">
            <Badge variant="secondary" data-id="7dejyev4q" data-path="src/components/ai/ConversationViewer.tsx">
              {messages.length} messages
            </Badge>
            <Button size="sm" variant="outline" onClick={copyConversation} data-id="v1tab2c8j" data-path="src/components/ai/ConversationViewer.tsx">
              <Copy className="h-4 w-4" data-id="sr6xzzjn5" data-path="src/components/ai/ConversationViewer.tsx" />
            </Button>
            <Button size="sm" variant="outline" onClick={downloadConversation} data-id="xepyg681k" data-path="src/components/ai/ConversationViewer.tsx">
              <Download className="h-4 w-4" data-id="sdhuxivh3" data-path="src/components/ai/ConversationViewer.tsx" />
            </Button>
            {onContinueConversation &&
            <Button
              size="sm"
              onClick={() => onContinueConversation(conversationId)}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white" data-id="megj650ir" data-path="src/components/ai/ConversationViewer.tsx">

                Continue Chat
              </Button>
            }
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0" data-id="vxhjdoalz" data-path="src/components/ai/ConversationViewer.tsx">
        <ScrollArea className="h-[600px] p-4" data-id="hj3upha1u" data-path="src/components/ai/ConversationViewer.tsx">
          {messages.length === 0 ?
          <div className="text-center text-gray-500 py-8" data-id="fgrf821n4" data-path="src/components/ai/ConversationViewer.tsx">
              <p data-id="qngj04qc1" data-path="src/components/ai/ConversationViewer.tsx">No messages in this conversation.</p>
            </div> :

          <div className="space-y-6" data-id="rkqcbiugu" data-path="src/components/ai/ConversationViewer.tsx">
              {messages.map((message, index) =>
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`flex ${message.message_type === 'user' ? 'justify-end' : 'justify-start'}`} data-id="xhx5g3nia" data-path="src/components/ai/ConversationViewer.tsx">

                  <div className={`max-w-[80%] ${message.message_type === 'user' ? 'order-2' : 'order-1'}`} data-id="c8w5yptst" data-path="src/components/ai/ConversationViewer.tsx">
                    <div className={`rounded-lg px-4 py-3 ${
                message.message_type === 'user' ?
                'bg-gradient-to-r from-indigo-600 to-purple-600 text-white' :
                'bg-gray-100 text-gray-800'}`
                } data-id="vrmocfyet" data-path="src/components/ai/ConversationViewer.tsx">
                      <div className="flex items-start gap-3" data-id="o0clgt3qo" data-path="src/components/ai/ConversationViewer.tsx">
                        <div className="flex-shrink-0" data-id="q1gzx35nd" data-path="src/components/ai/ConversationViewer.tsx">
                          {message.message_type === 'user' ?
                      <User className="h-5 w-5 mt-0.5" data-id="95w12nmvg" data-path="src/components/ai/ConversationViewer.tsx" /> :

                      <Bot className="h-5 w-5 mt-0.5" data-id="das4ktl2k" data-path="src/components/ai/ConversationViewer.tsx" />
                      }
                        </div>
                        <div className="flex-1" data-id="gqxh01qd9" data-path="src/components/ai/ConversationViewer.tsx">
                          <p className="whitespace-pre-wrap break-words" data-id="5bp17s8a2" data-path="src/components/ai/ConversationViewer.tsx">
                            {message.message_content}
                          </p>
                          <p className={`text-xs mt-2 ${
                      message.message_type === 'user' ? 'text-indigo-100' : 'text-gray-500'}`
                      } data-id="6uv9j4h1j" data-path="src/components/ai/ConversationViewer.tsx">
                            {formatTimestamp(message.timestamp)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
            )}
            </div>
          }
        </ScrollArea>
      </CardContent>
    </Card>);

};

export default ConversationViewer;